import {Component, Inject, OnInit, ViewChild, forwardRef} from '@angular/core';

import { StateMap, UnitOfWork, UserManager } from '../../services/common';
import { UtilFns } from '../../utils/common';
import { HttpClient } from '@angular/common/http';
import {Observable, Subject} from 'rxjs';

import {Material, MaterialComponent, MaterialComposition, MaterialCompositionDescription} from '../../entities/EntityModels';
import { MaterialSelectorComponent } from '../material/material-selector.component';
import {NCSManagerData, NCSManagerComponent} from './ncs-manager.component';

import * as _ from 'lodash';

export class NCSEditData {
    inCommentSubEditor: boolean;
    inSubEditor: boolean;
    adding: boolean;
    deleteFinished: boolean;
    deleting: boolean;
    editing: boolean;
    entity: MaterialComposition;
    entityComment: MaterialCompositionDescription;
    materialId: number;
    selectedMaterial: Material;
}

@Component({
    selector: 'ncs-viewer',
    templateUrl: './ncs-viewer.html',
})
export class NCSViewerComponent implements OnInit {
    @ViewChild(MaterialSelectorComponent, { static: true }) _materialSelectorComponent: MaterialSelectorComponent;

    _materials: Material[];

    public _isLoading = false;
    public _validationMessage: string;

    public _selectedMaterialCompositions: MaterialComposition[];
    public _selectedMaterialPartOfCompositions: MaterialComposition[];
    public _componentAmountTotal = 0;

    public _data: NCSEditData = new NCSEditData();
    public _parentData: NCSManagerData;

    _documentManagementUrl: string;

    constructor(public _stateMap: StateMap, private _httpClient: HttpClient, public _uow: UnitOfWork, public _userManager: UserManager,
        @Inject(forwardRef(() => NCSManagerComponent)) public _parent: NCSManagerComponent) {
        this._parentData = _parent._data;
    }

    ngOnInit() {
        if (this._parentData.materialId != null && this._parentData.materialId > 0) {
            this.showMaterialComponentData(this._parentData.materialId);
        }

        this._uow.getDocumentManagementUrl().then(url => {
            this._stateMap.documentManagementBaseUrl = url;
            this._documentManagementUrl = this._stateMap.documentManagementBaseUrl  + '/Report';
        });
    }

    canAdd() {
        return (this._data.selectedMaterial != null);
    }

    canDeactivate() {
        return (!this._uow.hasChanges());
    }

    get utilFns() {
        return UtilFns;
    }

    rowBackgroundColor(hasErrors: boolean): any {
        return (hasErrors) ? '#FFF0F5' : '#FFFFFF';
    }

    onAdd() {
        const newcomposition            = this._uow.materialCompositionFactory.create();
        newcomposition.materialId       = this._data.selectedMaterial.materialId;
        newcomposition.componentAmount  = 0;

        this._data.entity       = newcomposition;
        this._data.adding       = true;
        this._data.inSubEditor  = true;
    }

    onEdit(mcp: MaterialComposition) {
        this._data.entity       = mcp;
        this._data.inSubEditor  = true;
        this._data.editing      = true;
    }

    onRemove(mcp: MaterialComposition) {
        this._data.entity       = mcp;
        this._data.inSubEditor  = true;
        this._data.deleting     = true;
    }

    // ************************************************************************
    // *NCS Comment Actions
    // ************************************************************************
    hasNCSComment() {
        return (this._data.entityComment != null);
    }

    onAddNCSComment() {
        const newComment        = this._uow.materialCompositionDescriptionFactory.create();
        newComment.materialId   = this._data.selectedMaterial.materialId;

        this._data.entityComment        = newComment;
        this._data.adding               = true;
        this._data.inCommentSubEditor   = true;
    }

    onEditNCSComment() {
        this._data.inCommentSubEditor   = true;
        this._data.editing              = true;
    }

    onDeleteNCSComment() {
        this._data.inCommentSubEditor   = true;
        this._data.deleting             = true;
    }

    // ************************************************************************
    // *Fetch existing composition and component data.
    // ************************************************************************
    fetchMaterialCompositions(materialId: number) {
        this._selectedMaterialCompositions  = [];
        this._componentAmountTotal          = 0;

        const pMatId = {materialId: materialId};
        this._uow.fetch('Materials/MaterialCompositions', pMatId).then(r => {
            if (r != null && r.length > 0) {
                this._selectedMaterialCompositions = r;
                this._componentAmountTotal = this._selectedMaterialCompositions.map(a => a.componentAmount).reduce((a, b) => a + b);
            }
            return;
        });
    }

    fetchMaterialCompositionsByComponent(materialId: number) {
        this._selectedMaterialPartOfCompositions = [];
        const pMatId = {componentMaterialId: materialId};
        this._uow.fetch('Materials/MaterialCompositionsByComponentMaterial', pMatId).then(r => {
            if (r != null && r.length > 0) {
                this._selectedMaterialPartOfCompositions = r;
            }
        });
    }

    fetchMaterialCompositionDescription(materialId: number) {
        this._data.entityComment  = null;

        const pMatId = {materialId: materialId};
        return this._uow.fetch('Materials/MaterialCompositionDescriptionByMaterialId', pMatId).then(d => {
            if (d != null && d.length > 0) {
                this._data.entityComment = d[0];
            }
        });
    }

    // ************************************************************************
    // * material search functions
    // ************************************************************************
    public onSelectMaterial() {
        UtilFns.showModal(this._materialSelectorComponent, this).then(mli => {
            if (mli == null) {
                return;
            }
            return this._uow.materialRepository.withId(mli.materialId);
        }).then(m => {
            if (m) {
                this._data.selectedMaterial = m;
                this._parentData.materialId = m.materialId;
                this.fetchMaterialCompositions(this._data.selectedMaterial.materialId);
                this.fetchMaterialCompositionsByComponent(this._data.selectedMaterial.materialId);
                this.fetchMaterialCompositionDescription(this._data.selectedMaterial.materialId);
            }
        });
    }

    refreshMaterialComponentData() {
        if (this._data.selectedMaterial == null) { return; }

        this._isLoading = true;

        this.fetchMaterialCompositions(this._data.selectedMaterial.materialId);
        this.fetchMaterialCompositionsByComponent(this._data.selectedMaterial.materialId);

        this._isLoading = false;
    }

    // material selected from the component candidate list.
    showMaterialComponentData(materialId: number) {
        this._isLoading = true;

        this._uow.materialRepository.withId(materialId).then(m => {
            if (m) {
                this._data.selectedMaterial = m;
                this.fetchMaterialCompositions(this._data.selectedMaterial.materialId);
                this.fetchMaterialCompositionsByComponent(this._data.selectedMaterial.materialId);
                this.fetchMaterialCompositionDescription(this._data.selectedMaterial.materialId);

                this._isLoading = false;
            } else {
                this._validationMessage = 'Unexpected error fetching the selected Material.';
            }
        });
    }

    downloadMaterialComponentReport() {
        const fileName = 'NCSComponentList_' + this._data.selectedMaterial.materialId + '.csv';
        return this.exportReportFile(fileName, this._data.selectedMaterial.materialId, 'componentlist');
    }

    downloadMaterialComponentOfReport() {
        const fileName = 'NCSComponentOfReportFile_' + this._data.selectedMaterial.materialId + '.csv';
        return this.exportReportFile(fileName, this._data.selectedMaterial.materialId, 'component_of_list');
    }

    // ************************************************************************
    // document management web api url
    // ************************************************************************
    getDownloadFileURL(materialId: number, reportType: string): Observable<any> {
        let url: string;

        url = (reportType == 'componentlist') ? this._documentManagementUrl + '/GetNCSComponentListReportFile' : this._documentManagementUrl + '/GetNCSComponentOfReportFile';

        url = url + '?materialId=' +  materialId;

        return this._httpClient.get<any>(url, { responseType: 'blob' as 'json'});
    }

    public exportReportFile(fileName: string, materialId: number, reportType: string) {
        this._isLoading = true;
        this._validationMessage = '';
        this.getDownloadFileURL(materialId, reportType)
            .subscribe(
                (response: Blob) => {
                    const data = response;

                    const file = new Blob([data], { type: 'text/plain;charset=utf-8;' });
                    const fileURL = URL.createObjectURL(file);

                    const a         = document.createElement('a');
                    a.href        = fileURL;
                    a.target      = '_blank';
                    a.download    = fileName;
                    document.body.appendChild(a);
                    a.click();

                    a.remove();

                    window.URL.revokeObjectURL(fileURL);

                    this._validationMessage = 'File download successful.';

                },
                (error) => {
                    console.error('Request failed with error: ' + error.message);
                    this._validationMessage = error.message;
                    this._isLoading = false;
                },
                () => {
                    this._isLoading = false;
                });
    }
}

